/* iter92 — "Mes absences" page for staff/teacher accounts.
 * Fetches /api/staff-absences/mine and lets the user justify their own
 * pending absences without needing to open the email/SMS link.
 */
import { useState, useEffect, useCallback } from 'react';
import imageCompression from 'browser-image-compression';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import {
  CalendarBlank, WarningCircle, CheckCircle, XCircle, Clock, PaperclipHorizontal,
} from '@phosphor-icons/react';

const fmtFCFA = (v) =>
  (Number(v) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })
    .replace(/,/g, ' ') + ' FCFA';

const STATUS_META = {
  pending: { color: 'bg-amber-50 border-amber-200 text-amber-700',
              Icon: Clock, labelFR: 'En attente', labelEN: 'Pending' },
  justified: { color: 'bg-emerald-50 border-emerald-200 text-emerald-700',
                 Icon: CheckCircle, labelFR: 'Justifiée', labelEN: 'Justified' },
  rejected: { color: 'bg-red-50 border-red-200 text-red-700',
                Icon: XCircle, labelFR: 'Retenue', labelEN: 'Deducted' },
};

const KpiTile = ({ tone, label, value, testid }) => (
  <div className={`rounded-lg border p-3 ${tone}`} data-testid={testid}>
    <div className="text-2xl font-outfit font-bold">{value}</div>
    <div className="text-xs mt-0.5 opacity-80">{label}</div>
  </div>
);

const MyAbsences = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isVigil = user?.role === 'vigil';
  const isFR = language === 'fr';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Inline justification form state, keyed by absence id.
  const [drafts, setDrafts] = useState({});
  const [busyId, setBusyId] = useState('');
  const [noRecord, setNoRecord] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/staff-absences/mine');
      setData(data);
    } catch (e) {
      if (e?.response?.status === 404) { setNoRecord(true); return; }
      toast.error(e?.response?.data?.detail
        || (isFR ? 'Erreur chargement' : 'Load error'));
    } finally { setLoading(false); }
  }, [isFR]);

  useEffect(() => { load(); }, [load]);

  const setDraft = (id, patch) =>
    setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), ...patch } }));

  const submit = async (id) => {
    const draft = drafts[id] || {};
    if (!draft.reason || !draft.reason.trim()) {
      toast.error(isFR ? 'Motif requis' : 'Reason required');
      return;
    }
    setBusyId(id);
    try {
      if (draft.file) {
        // Multipart upload endpoint — attaches the scanned certificate.
        const fd = new FormData();
        fd.append('reason', draft.reason.trim());
        fd.append('file', draft.file);
        await api.post(`/staff-absences/${id}/self-justify-upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post(`/staff-absences/${id}/self-justify`, {
          reason: draft.reason.trim(),
        });
      }
      toast.success(isFR
        ? 'Justification envoyée à la RH'
        : 'Justification submitted');
      setDrafts((d) => { const c = { ...d }; delete c[id]; return c; });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || (isFR ? 'Erreur envoi' : 'Submit error'));
    } finally { setBusyId(''); }
  };

  if (loading && !data) {
    return (
      <div className="p-6" data-testid="my-absences-loading">
        <p className="text-[#94A3B8]">
          {isFR ? 'Chargement de vos absences…' : 'Loading your absences…'}
        </p>
      </div>
    );
  }
  if (noRecord) {
    return (
      <div className="p-6" data-testid="my-absences-no-record">
        {isVigil && (
          <button
            onClick={() => navigate('/security')}
            className="text-sm text-[#0052FF] hover:underline inline-flex items-center gap-1 mb-3"
            data-testid="vigil-back-to-security"
          >
            ← {isFR ? 'Retour au poste de garde' : 'Back to gate post'}
          </button>
        )}
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          {isFR
            ? "Aucune fiche personnel ne correspond à votre adresse e-mail. La liaison est automatique : demandez à la RH d'utiliser la même adresse e-mail sur votre fiche personnel pour voir vos absences à justifier."
            : 'No staff record matches your e-mail address. Linking is automatic: ask HR to use the same e-mail on your staff record to see your absences.'}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const c = data.counts || {};
  const items = data.items || [];
  const pending = items.filter((a) => a.status === 'pending');
  const others = items.filter((a) => a.status !== 'pending');

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-4"
      data-testid="my-absences-page">
      {isVigil && (
        <button
          onClick={() => navigate('/security')}
          className="text-sm text-[#0052FF] hover:underline inline-flex items-center gap-1"
          data-testid="vigil-back-to-security"
        >
          ← {isFR ? 'Retour au poste de garde' : 'Back to gate post'}
        </button>
      )}
      <div>
        <h1 className="font-outfit text-2xl font-bold flex items-center gap-2">
          <CalendarBlank size={26} className="text-[#0052FF]" weight="duotone" />
          {isFR ? 'Mes absences' : 'My absences'}
        </h1>
        <p className="text-sm text-[#64748B] mt-1">
          {isFR
            ? 'Consultez et justifiez vos absences avant clôture de la paie.'
            : 'View and justify your absences before payroll cutoff.'}
        </p>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-4 gap-2">
        <KpiTile tone="bg-amber-50 border-amber-200 text-amber-700"
          value={c.pending || 0}
          label={isFR ? 'En attente' : 'Pending'}
          testid="kpi-pending" />
        <KpiTile tone="bg-emerald-50 border-emerald-200 text-emerald-700"
          value={c.justified || 0}
          label={isFR ? 'Justifiées' : 'Justified'}
          testid="kpi-justified" />
        <KpiTile tone="bg-red-50 border-red-200 text-red-700"
          value={c.rejected || 0}
          label={isFR ? 'Retenues' : 'Deducted'}
          testid="kpi-rejected" />
        <KpiTile tone="bg-slate-50 border-slate-200 text-slate-700"
          value={c.total || 0}
          label={isFR ? 'Total' : 'Total'}
          testid="kpi-total" />
      </div>

      {/* Pending — justification forms */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="font-outfit text-base flex items-center gap-2">
            <WarningCircle size={16} className="text-amber-600" weight="duotone" />
            {isFR ? 'À justifier' : 'To justify'}
            <span className="text-xs text-[#94A3B8] font-normal ml-1">
              ({pending.length})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {pending.length === 0 && (
            <p className="text-sm text-[#94A3B8] italic"
              data-testid="no-pending">
              ✅ {isFR ? 'Aucune absence en attente. Rien à justifier.'
                : 'No pending absences. Nothing to justify.'}
            </p>
          )}
          {pending.map((a) => {
            const draft = drafts[a.id] || {};
            const alreadySubmitted = a.employee_justified;
            return (
              <div key={a.id}
                data-testid={`pending-item-${a.id}`}
                className="border border-amber-200 rounded-lg p-3 bg-amber-50/40">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-semibold text-[#0F172A]">
                      {a.date} · {a.hours_missed}h {isFR ? 'manquantes' : 'missed'}
                    </div>
                    <div className="text-[11px] text-[#94A3B8]">
                      {isFR ? 'Retenue potentielle' : 'Potential deduction'}
                      : <b className="text-red-700">
                        {fmtFCFA(a.expected_deduction_fcfa || 0)}
                      </b>
                    </div>
                  </div>
                  {alreadySubmitted && (
                    <span className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5"
                      data-testid={`already-sent-${a.id}`}>
                      {isFR ? '✉️ Justificatif envoyé — attente RH'
                        : '✉️ Submitted — awaiting HR'}
                    </span>
                  )}
                </div>
                {!alreadySubmitted && (
                  <div className="mt-3 space-y-2">
                    <Textarea
                      value={draft.reason || ''}
                      onChange={(e) => setDraft(a.id, { reason: e.target.value })}
                      rows={2}
                      placeholder={isFR
                        ? 'Motif détaillé (maladie, deuil, imprévu…)'
                        : 'Detailed reason (illness, bereavement…)'}
                      className="border-[#E2E8F0]"
                      data-testid={`reason-input-${a.id}`}
                    />
                    {/* iter92 — Direct file upload (image/PDF, ≤5 MB).
                        Perfect for scanning a medical cert from smartphone. */}
                    <label className="flex items-center gap-2 border-2 border-dashed border-[#CBD5E1] rounded-lg p-2 cursor-pointer hover:bg-slate-50 transition"
                      data-testid={`file-picker-${a.id}`}>
                      <PaperclipHorizontal size={16} className="text-[#0052FF]" />
                      <span className="text-xs text-[#475569] flex-1 truncate">
                        {draft.file
                          ? (draft.file.size < 1024
                            ? `📎 ${draft.file.name} (${draft.file.size} o)`
                            : `📎 ${draft.file.name} (${(draft.file.size / 1024).toFixed(1)} Ko)`)
                          : (isFR
                            ? '📸 Photo ou PDF du justificatif (optionnel, ≤5 Mo)'
                            : '📸 Photo or PDF (optional, ≤5 MB)')}
                      </span>
                      {draft.file && (
                        <button type="button"
                          onClick={(e) => { e.preventDefault(); setDraft(a.id, { file: null }); }}
                          className="text-xs text-red-600 hover:underline"
                          data-testid={`clear-file-${a.id}`}
                        >
                          ✕
                        </button>
                      )}
                      <input type="file" className="hidden"
                        accept="image/*,application/pdf,.pdf"
                        capture="environment"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          let out = f;
                          // iter93 — Auto-compress large photos client-side.
                          // Only images >2 MB are compressed; PDFs and small
                          // files pass through unchanged. Keeps EXIF-clean
                          // JPEGs, targets 1.5 MB max and 1920px on the long
                          // side (plenty for a scanned certificate).
                          if (f.type.startsWith('image/')
                            && f.size > 2 * 1024 * 1024) {
                            toast.info(isFR
                              ? `Compression de la photo (${(f.size / 1024 / 1024).toFixed(1)} Mo)…`
                              : `Compressing photo (${(f.size / 1024 / 1024).toFixed(1)} MB)…`);
                            try {
                              out = await imageCompression(f, {
                                maxSizeMB: 1.5,
                                maxWidthOrHeight: 1920,
                                useWebWorker: true,
                                fileType: 'image/jpeg',
                                initialQuality: 0.85,
                              });
                              // Rewrap so `.name` stays user-friendly.
                              const cleanName = f.name.replace(/\.[^.]+$/, '') + '.jpg';
                              out = new File([out], cleanName,
                                { type: out.type || 'image/jpeg' });
                              toast.success(isFR
                                ? `Photo compressée : ${(out.size / 1024).toFixed(0)} Ko`
                                : `Compressed to ${(out.size / 1024).toFixed(0)} KB`);
                            } catch (err) {
                              console.warn('compression failed', err);
                              toast.error(isFR
                                ? 'Compression impossible — fichier original conservé'
                                : 'Compression failed — using original file');
                              out = f;
                            }
                          }
                          if (out.size > 5 * 1024 * 1024) {
                            toast.error(isFR ? 'Fichier trop volumineux (>5 Mo)' : 'File too large (>5 MB)');
                            e.target.value = '';
                            return;
                          }
                          setDraft(a.id, { file: out });
                        }}
                        data-testid={`file-input-${a.id}`}
                      />
                    </label>
                    <div className="flex items-center justify-end">
                      <Button
                        size="sm"
                        onClick={() => submit(a.id)}
                        disabled={busyId === a.id}
                        className="bg-[#0052FF] hover:bg-[#0040CC]"
                        data-testid={`submit-btn-${a.id}`}
                      >
                        {busyId === a.id
                          ? (isFR ? 'Envoi…' : 'Sending…')
                          : (isFR ? 'Envoyer le justificatif' : 'Submit')}
                      </Button>
                    </div>
                  </div>
                )}
                {alreadySubmitted && a.reason && (
                  <div className="mt-2 text-xs text-[#475569] space-y-1">
                    <div>
                      <b>{isFR ? 'Motif transmis' : 'Reason submitted'} : </b>
                      {a.reason}
                    </div>
                    {a.attachment_name && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/staff-absences/${a.id}/attachment`}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[#0052FF] hover:underline"
                        data-testid={`view-attachment-${a.id}`}
                      >
                        <PaperclipHorizontal size={12} />
                        {isFR ? 'Voir la pièce jointe' : 'View attachment'}
                        <span className="text-[#94A3B8]">
                          ({a.attachment_name})
                        </span>
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* History — read-only */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="font-outfit text-base">
            {isFR ? 'Historique' : 'History'}
            <span className="text-xs text-[#94A3B8] font-normal ml-2">
              ({others.length})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {others.length === 0 && (
            <p className="text-sm text-[#94A3B8] italic">
              {isFR ? 'Aucun historique pour l\'instant.' : 'No history yet.'}
            </p>
          )}
          {others.map((a) => {
            const meta = STATUS_META[a.status] || STATUS_META.pending;
            const I = meta.Icon;
            return (
              <div key={a.id}
                data-testid={`history-item-${a.id}`}
                className={`border rounded-lg p-3 flex items-center justify-between gap-2 ${meta.color}`}>
                <div className="flex items-center gap-2">
                  <I size={18} weight="duotone" />
                  <div>
                    <div className="font-semibold text-[#0F172A] text-sm">
                      {a.date} · {a.hours_missed}h
                    </div>
                    <div className="text-[11px] text-[#475569]">
                      {isFR ? meta.labelFR : meta.labelEN}
                      {a.reviewer_comment ? ` — ${a.reviewer_comment}` : ''}
                    </div>
                  </div>
                </div>
                {a.status === 'rejected' && (
                  <div className="text-right">
                    <div className="text-[10px] text-[#94A3B8] uppercase">
                      {isFR ? 'Retenue' : 'Deducted'}
                    </div>
                    <div className="text-sm font-outfit font-bold text-red-700">
                      {fmtFCFA(a.expected_deduction_fcfa || 0)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyAbsences;
